import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import fetchWithCredentials from "apiClient";
import { PendingContact } from "types/PendingContact";

interface PendingContactsContextType {
  pendingContacts: PendingContact[];
  loading: boolean;
  error: string | null;
  refresh: (condoId: number | null) => Promise<void>;
  pendingContactsCount: number;
  selectedContactId: number | null;
  setSelectedContactId: (contactId: number | null) => void;
  markContactAsRead: (contactId: number) => void;
  updateLastReadAt: (contactId: number, lastReadAt: string) => void;
  removePendingContact: (pendingContactId: number) => void;
}

const PendingContactsContext = createContext<
  PendingContactsContextType | undefined
>(undefined);

export function PendingContactsProvider({ children }: { children: ReactNode }) {
  const [pendingContacts, setPendingContacts] = useState<PendingContact[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null
  );

  const fetchPendingContacts = useCallback(async (condoId: number | null) => {
    if (!condoId) {
      setPendingContacts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const pendingContacts = await fetchWithCredentials(
        `/manager-api/pending-contacts/?condo=${condoId}`
      );
      // Transform the API response to match our type
      const transformed = pendingContacts.map((pc: any) => ({
        id: pc.id,
        contact: pc.contact,
        identificationDetails: pc.identificationDetails,
        lastMessageAt: pc.last_message_at,
        hasUnread: pc.has_unread,
        lastReadAt: pc.last_read_at,
        replyMute: pc.replyMute ?? null,
      }));
      setPendingContacts(transformed);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching pending contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(
    async (condoId: number | null) => {
      await fetchPendingContacts(condoId);
    },
    [fetchPendingContacts]
  );

  const markContactAsRead = useCallback((contactId: number) => {
    setPendingContacts((prev) =>
      prev.map((pendingContact) =>
        pendingContact.contact.id === contactId
          ? { ...pendingContact, hasUnread: false }
          : pendingContact
      )
    );
  }, []);

  const updateLastReadAt = useCallback(
    (contactId: number, lastReadAt: string) => {
      setPendingContacts((prev) =>
        prev.map((pendingContact) =>
          pendingContact.contact.id === contactId
            ? { ...pendingContact, lastReadAt }
            : pendingContact
        )
      );
    },
    []
  );

  const removePendingContact = useCallback((pendingContactId: number) => {
    setPendingContacts((prev) =>
      prev.filter((pendingContact) => pendingContact.id !== pendingContactId)
    );
  }, []);

  const pendingContactsCount = useMemo(
    () => pendingContacts.length,
    [pendingContacts]
  );

  return (
    <PendingContactsContext.Provider
      value={{
        pendingContacts,
        loading,
        error,
        refresh,
        pendingContactsCount,
        selectedContactId,
        setSelectedContactId,
        markContactAsRead,
        updateLastReadAt,
        removePendingContact,
      }}
    >
      {children}
    </PendingContactsContext.Provider>
  );
}

export function usePendingContacts() {
  const context = useContext(PendingContactsContext);
  if (!context) {
    throw new Error(
      "usePendingContacts must be used within a PendingContactsProvider"
    );
  }
  return context;
}
